<template>
  <div>
    <div class="row">
      <div class="col-12">
        <ReportFilterPopover
          class="float-right"
          headerTitle="Price List Filter"
          :showApplyButton="true"
          :showResetButton="true"
          @apply="applyPricingFilter"
          @reset="resetPricingFilter">
          <template v-slot:popover>
            <ReportFilterGroup label="Price Update">
              <PriceRequestSearchableSelect
                v-model="editingPricingFilter.stockPriceRequestGroupId"
                @updateRequestInfo="editingPricingFilter.stockPriceRequestInfo = $event"
                :statusFilters="[PricingRequestStatusTypes.draft, PricingRequestStatusTypes.pendingApproval, PricingRequestStatusTypes.pendingSchedule]"
                :nullSelectionEnabled="true"
              />
            </ReportFilterGroup>
            <ReportFilterGroup label="Account Location">
              <AccountLocationMultiSelect v-model="editingPricingFilter.accountLocationIdList" :activeOnly="true" />
            </ReportFilterGroup>
          </template>
          <template v-slot:filterDisplay>
            <div v-if="appliedPricingFilter.stockPriceRequestGroupId">
              <i class="far fa-money-check-edit-alt mr-2"></i>{{appliedPricingFilter.stockPriceRequestInfo ? appliedPricingFilter.stockPriceRequestInfo.sequenceNumber : 'None'}}
            </div>
            <ReportFilterDisplayAccountLocation :accountLocationId="appliedPricingFilter.accountLocationIdList" />
          </template>
        </ReportFilterPopover>

        <ContentTitle>Price List Overrides Report</ContentTitle>
        <ContentSubTitle>Displays price lists and pricing overrides for all contacts</ContentSubTitle>
      </div>
    </div>
    <div class="row mt-2">
      <div class="col-12">

        <LoadingPanel :loading="loading" :loadingError="loadingError" />
        <div v-if="!loading">
          <PivotDataTable
            :dataset="dataset"
            :columns="columns"
            :rowFields="rowFields"
            :fields="fields">
            <template v-slot:rowfield="info">
              <strong v-if="info.data.column.field[0] == 'name'">
                <LinkContact :id="info.data.row.cellData.id" :name="info.data.row.cellData.name" />
              </strong>
              <strong v-if="info.data.column.field[0] == 'accountLocationId'">
                <LabelAccountLocationBadge :accountLocationId="info.data.row.cellData.accountLocationId" />
              </strong>
              <div v-if="info.data.column.field[0] == 'customerPriceListId'">
                <LinkPriceList :id="info.data.row.cellData.customerPriceListId" :customerId="info.data.row.cellData.id" name="Price List" />
              </div>
              <div v-if="info.data.column.field[0] == 'defaultSalesRateName'">{{info.data.row.cellData.defaultSalesRateName}}</div>
            </template>
            <template v-slot:cell="cell">
              {{formatHyphenEmptyValue(cell.data)}}
            </template>
            <template v-slot:empty>
              No price list data found
            </template>
          </PivotDataTable>
        </div>
      </div>
    </div>
  </div>
</template>


<script>
import _ from 'lodash'
import PivotDataTable from '@/components/table/pivotDataTable'
import FormInputStockCode from '@/modules/inventory/components/formInputStockCode'
import AccountLocationMultiSelect from '@/modules/admin/components/accountLocationMultiSelect'
import ReportFilterDisplayAccountLocation from '@/components/reporting/reportFilterDisplayAccountLocation'
import PriceRequestSearchableSelect from '@/modules/pricing/components/priceRequestSearchableSelect'

import ReportFilterPopover from '@/components/reporting/reportFilterPopover'
import ReportDateRangeSelect from '@/components/reporting/reportDateRangeSelect'
import ReportFilterGroup from '@/components/reporting/reportFilterGroup'

import { PriceRequestTypes, PriceRequestTypeLookup, PricingRequestStatusTypes, PricingRequestStatusTypeLookup } from '@/store/types'

export default {
  name: 'CustomerPricingReport',
  components: {
    PivotDataTable,
    FormInputStockCode,
    PriceRequestSearchableSelect,
    ReportFilterPopover,
    ReportDateRangeSelect,
    ReportFilterGroup,
    AccountLocationMultiSelect,
    ReportFilterDisplayAccountLocation
  },
  data() {
    return {
      dataset: [],
      columns: [
        { field: 'stockPriceGroupName', sortField: 'stockPriceGroupSortOrder' },
      ],
      rowFields: [
        { field: 'name', name: 'Contact' },
        { field: 'accountLocationId', name: 'Location' },
        { field: 'customerPriceListId', name: 'Price List' },
        { field: 'defaultSalesRateName', name: 'Default Sales Rate' }
      ],
      fields: [
        { field: 'totalQuantity' }
      ],
      loading: false,
      loadingError: '',

      defaultPricingFilter: {    
        stockPriceRequestGroupId: null,
        stockPriceRequestInfo: null,
        accountLocationIdList: []
      },
      editingPricingFilter: {},
      appliedPricingFilter: {},
    }
  },
  computed: {
    
  },
  created() {
    this.PriceRequestTypes = PriceRequestTypes
    this.PricingRequestStatusTypes = PricingRequestStatusTypes
  },
  mounted() {
    this.editingPricingFilter = _.clone(this.defaultPricingFilter)
    this.appliedPricingFilter = _.clone(this.defaultPricingFilter)

    this.requestReportData()
  },
  beforeDestroy() {            
  },
  methods: {
    requestReportData() {
      this.dataset = []

      var params = {
        stockPriceRequestGroupId: this.appliedPricingFilter.stockPriceRequestGroupId || 0,
        accountLocationId: this.appliedPricingFilter.accountLocationIdList
      }

      this.loading = true
      this.loadingError = ''
      this.$apiService.analyticsService.getCustomerPriceListReport(params)
      .then(response => {
        let dataList = response.data || []

        // Transform data into dataset
        dataList.forEach(contactInfo => {
          if(_.isArray(contactInfo.priceListInfo)) {
            contactInfo.priceListInfo.forEach(priceListInfo => {
              this.dataset.push({
                id: contactInfo.id,
                name: contactInfo.name,
                accountLocationId: contactInfo.accountLocationId,
                customerPriceListId: priceListInfo.customerPriceListId,
                defaultSalesRateName: contactInfo.defaultSalesRateName,
                stockPriceGroupName: priceListInfo['stockPriceGroup.name'] || 'Other',
                stockPriceGroupSortOrder: priceListInfo['stockPriceGroup.sortOrder'] || 9999,
                totalQuantity: priceListInfo.totalQuantity
              })
            })
          }
        })
      })    
      .catch(err => {
        console.error(err)
        this.loadingError = 'Error loading data'
      })
      .finally(() => {
        this.loading = false
      })
    },
    applyPricingFilter() {
      this.appliedPricingFilter = _.clone(this.editingPricingFilter)

      this.requestReportData()
    },
    resetPricingFilter() {
      this.editingPricingFilter = _.clone(this.defaultPricingFilter)
      this.appliedPricingFilter = _.clone(this.defaultPricingFilter)

      this.requestReportData()
    }
  }
}
</script>
